/**
 * The fighter — what the world remembers about somebody who can be hurt.
 *
 * Split from `combat.ts` (which holds the rules) so the dependency graph stays
 * a line rather than a knot: world state has to name this type, and the rules
 * have to read world state. State below, rules above, and nothing points back
 * down.
 *
 * The three resources a fight is fought with all live here, and none of them is
 * a hit-point bar:
 *
 * - **Dice** come from the body — M1's `effectivePool`, plus skill, less what
 *   the armour weighs and what the lungs have left.
 * - **Stamina** is spent per swing and regained only when you are not swinging.
 *   It injures nobody; it makes wind-ups longer and dice fewer, which is what
 *   being tired actually costs.
 * - **Commitment** is the tick you are in: winding up, recovering, guarding, or
 *   free. It is what decides how much of the pool defends.
 *
 * Content is held by **id**. `cloneWorld` deep-copies the world ten times a
 * second, and a body plan copied onto every actor would be thousands of
 * allocations a tick to describe something that never changes — so the ids
 * resolve through the catalogue, which lives outside the world.
 */
import { createBody, deriveCapabilities, toughnessRating } from './body.js';
import { timingOf, } from './damage.js';
import { HUNGER_STAMINA } from './needs.js';
import { createVitals, effectivePool, isDown } from './vitals.js';
/* -------------------------------------------------------------------------- *
 * Stamina (M2-01, design spec §4.1 / AoD §7.5)
 * -------------------------------------------------------------------------- */
/** Stamina is stored in thousandths, for the reason blood and movement are. */
export const STAMINA_SCALE = 1000;
export const STAMINA_BASE = 20;
export const STAMINA_PER_TOUGHNESS = 10;
/** Regained per tick while doing nothing much. Halved while guarding. */
export const STAMINA_REGEN = 200;
export const GUARD_REGEN_FACTOR = 0.5;
/** Standing up after a knockdown is work. */
export const STAND_COST = 3;
/** Ticks a knocked-down fighter spends on the ground before they can rise. */
export const STAND_TICKS = 15;
/**
 * The wind. Bands rather than a curve because the player has to be able to
 * *read* their own state off one word in the log — "Gwil is winded" — and
 * because a continuous penalty is a continuous balance argument nobody ever
 * finishes.
 */
export const STAMINA_BANDS = [
    { id: 'fresh', floor: 0.6, dice: 0, windUp: 0 },
    { id: 'labouring', floor: 0.3, dice: -1, windUp: 1 },
    { id: 'winded', floor: 0, dice: -2, windUp: 2 },
];
const LAST_BAND = STAMINA_BANDS[STAMINA_BANDS.length - 1];
export function bandAt(fraction) {
    for (const band of STAMINA_BANDS) {
        if (fraction >= band.floor)
            return band;
    }
    return LAST_BAND;
}
/**
 * A fighter's full wind. Scaled by the `breath` capability, so a punctured lung
 * costs endurance rather than dice — which is the distinction M1 drew between
 * what a body can do and how long it can keep doing it.
 *
 * Hunger scales it too, since M3-06, and it is the *cap* that moves rather than
 * the regeneration rate: a hungry man does not run out of wind faster, he has
 * less of it to start with, and he finds that out in the first exchange instead
 * of the sixth.
 */
export function maxStamina(plan, vitals, attributes) {
    const breath = deriveCapabilities(plan, vitals.body).breath;
    const full = STAMINA_BASE + STAMINA_PER_TOUGHNESS * toughnessRating(attributes);
    // `?? 1` rather than an index, for a world saved before there was any such
    // thing as an appetite: an undefined band must read as fed, not as NaN.
    const hunger = HUNGER_STAMINA[vitals.hunger] ?? 1;
    return Math.max(1, Math.round(full * breath * hunger));
}
/**
 * The verbs that take time. Named here rather than in `interact.ts` because a
 * `Channel` is world state and world state may not import a rule module.
 */
export const CHANNEL_VERBS = [
    'staunch',
    'loot',
    // `drag` was here until M4-06 and is not a channel any more: taking hold is
    // instant, and what takes time afterwards is the walk home.
    'forage',
    // M3-06's camp verbs. All three work on ground or on a carcass rather than on
    // a body, so all three carry a `null` target — see `Channel.targetId`.
    'build',
    'butcher',
    'cook',
    // RC2-04. Chopping a deadfall down. A ground channel like the camp verbs — it
    // works on the fallen wood you are standing by, carries a `null` target, and a
    // blow can interrupt it the same way.
    'chop',
    // M4-03's preparation. Ground, like the rest of the camp: it works on a fire
    // and a satchel rather than on a body.
    'prepare',
    // M4's medicine. Each is real work taking real seconds that a blow can end —
    // the whole tension of first aid with the wolves still circling — and each
    // names a body (the patient, who may be the treater themselves).
    'bandage',
    'clean',
    'suture',
    'cauterize',
    // M4-05's surgery. Channelled like the rest of the ladder and for the same
    // reason — a knife in a wound is work a blow can interrupt — but far longer,
    // because the point of a procedure is that it commits the pair of you to a
    // place for the length of it.
    'extract',
    'amputate',
    // M4-07. Both work on a place rather than a body — a ground channel, like
    // foraging — and both take the better part of a minute with a spade.
    'bury',
    'dig',
];
/**
 * How somebody has hold of a body (M4-06).
 *
 * Three ways, and the differences are the whole ticket: a **drag** is one hand
 * and a body bumping along behind you, a **carry** is over the shoulder with
 * both arms full, and a **litter** is two people and the only one of the three
 * that does not hurt the patient.
 */
export const BURDEN_MODES = ['drag', 'carry', 'litter'];
/**
 * What each way of moving a body costs in speed (M4-06).
 *
 * The ticket's numbers, and the ordering is the argument: dragging is fastest
 * to *start* and slowest to travel, a shoulder carry is the compromise that
 * costs you your hands, and two people with a litter move at nearly walking
 * pace. The good answer needs three players, which is the point.
 */
/**
 * What moving quietly costs in speed (M5-03).
 *
 * Half, and the number is the mechanic: a stalk is a thing you commit *time*
 * to, and a hunter who could sneak at walking pace would sneak everywhere. It
 * multiplies into the step cost beside `burdenSpeed`, so creeping while
 * carrying a friend is as slow as both of those things should be together.
 */
export const SNEAK_SPEED = 0.5;
/** The multiplier a sneaking mover walks at — 1 for anybody upright and brisk. */
export function sneakSpeed(combatant) {
    return combatant?.sneaking === true ? SNEAK_SPEED : 1;
}
export const BURDEN_SPEED = {
    drag: 0.4,
    carry: 0.6,
    litter: 0.8,
};
/** The multiplier a burdened mover walks at — 1 for anybody with free hands. */
export function burdenSpeed(combatant) {
    const mode = combatant?.burden?.mode;
    return mode === undefined ? 1 : BURDEN_SPEED[mode];
}
/**
 * Stamina one burdened step costs, in thousandths.
 *
 * Scaled by what is being carried against the frame of whoever is carrying it,
 * per the ticket ("stamina drain scaled by patient weight vs carrier STR"). A
 * corpse or a carcass has no attributes to read and weighs `REFERENCE_FRAME` —
 * right for a corpse, and generous to whoever is hauling a stag.
 */
export function burdenStaminaCost(carrier, loadFrame) {
    const mode = carrier.burden?.mode;
    if (mode === undefined)
        return 0;
    const load = loadFrame ?? REFERENCE_FRAME;
    const strength = Math.max(1, carrier.loadout.attributes.frame);
    return Math.round(BURDEN_STAMINA[mode] * (load / strength));
}
/** What each way of holding somebody costs in wind, per step, in thousandths. */
export const BURDEN_STAMINA = {
    drag: 60,
    carry: 110,
    litter: 45,
};
/** The frame a burden's weight is measured against — an ordinary strong man. */
const REFERENCE_FRAME = 12;
/** Can this body still swing? Not with somebody over its shoulder (M4-06). */
export function handsFull(combatant) {
    const mode = combatant?.burden?.mode;
    return mode === 'carry' || mode === 'litter';
}
/** Anybody unnamed is `they`. A wrong guess is worse than a neutral default. */
export const DEFAULT_PRONOUNS = {
    subject: 'they',
    object: 'them',
    possessive: 'their',
};
export function createCombatant(options) {
    const vitals = options.vitals ?? createVitals(createBody(options.plan));
    return {
        loadout: options.loadout,
        pronouns: options.pronouns ?? DEFAULT_PRONOUNS,
        vitals,
        staminaMilli: maxStamina(options.plan, vitals, options.loadout.attributes) * STAMINA_SCALE,
        band: 'fresh',
        guarding: false,
        engagedWith: null,
        withdrawingUntil: 0,
        sneaking: false,
        swing: null,
        recoveryUntil: 0,
        queued: null,
        queuedAt: 0,
        defences: [],
        riposteAgainst: null,
        riposteUntil: 0,
        proneUntil: 0,
        channel: null,
        shieldWear: 0,
        consent: null,
        burden: null,
    };
}
export function kitOf(catalogue, combatant) {
    const { loadout } = combatant;
    const plan = catalogue.plan(loadout.planId);
    const weapon = catalogue.weapon(loadout.weaponId);
    if (!plan || !weapon)
        return null;
    const profile = catalogue.profile(weapon.profileId);
    if (!profile)
        return null;
    const armour = [];
    for (const id of loadout.armourIds) {
        const piece = catalogue.armour(id);
        if (!piece)
            continue;
        // Both hands on the haft: the shield is slung, and worth nothing while it
        // is. Resolved here rather than refused at the loadout, so a character
        // saved when the weapon was one-handed still loads.
        if (weapon.twoHanded && (piece.defenceDice ?? 0) > 0)
            continue;
        armour.push(piece);
    }
    return { plan, weapon, profile, armour, timing: timingOf(weapon) };
}
/* -------------------------------------------------------------------------- *
 * Dice and breath
 * -------------------------------------------------------------------------- */
/**
 * Share of the pool that defends, by what the fighter is doing (M2-02).
 *
 * Guard beats committed deliberately: a fighter who raises their guard *and*
 * swings gets the guard's defence and the guard's feeble attack, which is what
 * makes guard a decision rather than a free buff you leave on.
 */
export const DEFENCE_SHARE = {
    guard: 0.65,
    neutral: 0.5,
    /** Mid-wind-up or mid-recovery. Committed is exposed. */
    committed: 0.25,
};
/** Encumbrance: what the armour costs in dice, and in breath per swing. */
export function encumbranceOf(armour) {
    return armour.reduce((sum, piece) => sum + (piece.poolPenalty ?? 0), 0);
}
/**
 * Shields are consumable (M2-04).
 *
 * A limewood round shield is not a piece of equipment you own for a campaign;
 * it is a board that catches axes until it stops being a board. Historically
 * true, AoD-adjacent, and mechanically it is the thing that stops "carry a
 * shield" from being a decision made once at character creation: the dice it is
 * worth drain away over a long fight, and the fighter can feel it happening
 * because the log tells them each time it splinters.
 *
 * The fractions are of the shield's own `defenceDice`, so a buckler degrades
 * proportionally rather than surviving longer for being smaller.
 */
export const SHIELD_CONDITIONS = [
    { id: 'sound', dice: 1 },
    { id: 'notched', dice: 0.75 },
    { id: 'split', dice: 0.4 },
    { id: 'broken', dice: 0 },
];
export const SHIELD_BROKEN = SHIELD_CONDITIONS.length - 1;
export function shieldConditionAt(wear) {
    const clamped = Math.min(Math.max(0, Math.trunc(wear)), SHIELD_BROKEN);
    return SHIELD_CONDITIONS[clamped].id;
}
/** The piece carrying the defence dice, if the fighter has one. */
export function shieldOf(armour) {
    let best = null;
    for (const piece of armour) {
        if ((piece.defenceDice ?? 0) <= (best?.defenceDice ?? 0))
            continue;
        best = piece;
    }
    return best;
}
/**
 * The best shield in the kit, in defence dice, at the state it is in. Zero if
 * there is none — or if there is nothing left of it but the boss and the grip.
 */
export function shieldDice(armour, wear = 0) {
    const shield = shieldOf(armour);
    if (!shield)
        return 0;
    const clamped = Math.min(Math.max(0, Math.trunc(wear)), SHIELD_BROKEN);
    const condition = SHIELD_CONDITIONS[clamped];
    return Math.floor((shield.defenceDice ?? 0) * condition.dice);
}
/**
 * The chance one blocked blow costs the shield a step of its condition.
 *
 * Scaled by what hit it, so the criterion the ticket asks for — a shield that
 * visibly goes under *sustained axe blows* — falls out of the weapon rather
 * than out of a counter: a knife notches a board about as often as never, and a
 * Dane axe takes it apart in a handful of exchanges. Capped below 1 so no
 * single blow is guaranteed to break a shield outright; that reads as a bug to
 * the player on the receiving end, however heavy the axe was.
 */
export const SHIELD_WEAR_BASE = 0.1;
export const SHIELD_WEAR_PER_DR = 0.1;
export const SHIELD_WEAR_MAX = 0.6;
export function shieldWearChance(damageRating) {
    return Math.min(SHIELD_WEAR_MAX, Math.max(0, SHIELD_WEAR_BASE + SHIELD_WEAR_PER_DR * damageRating));
}
export function staminaFraction(combatant, kit) {
    const full = maxStamina(kit.plan, combatant.vitals, combatant.loadout.attributes);
    return combatant.staminaMilli / (full * STAMINA_SCALE);
}
/**
 * The dice this fighter brings, before the attack/defence split: AoD §7.3's
 * pool formula less the crew terms it had, plus the stamina band it did not.
 */
export function combatDice(combatant, kit) {
    const { loadout } = combatant;
    const base = effectivePool(kit.plan, combatant.vitals, loadout.attributes);
    const band = bandAt(staminaFraction(combatant, kit));
    return Math.max(0, base + loadout.skill - encumbranceOf(kit.armour) + band.dice);
}
/**
 * Helpless: senseless or dead, and therefore something others may act on
 * rather than someone who acts (M2-07).
 *
 * Here rather than in `interact.ts` because both the interaction verbs and the
 * movement and speech handlers need it, and `movement.ts` importing the
 * interaction rules to ask whether a man can walk would put a cycle in a module
 * graph the whole engine is arranged to keep a line.
 *
 * Distinct from `isDown`, which takes a plan the caller has to have resolved.
 * This is the same question asked of a combatant, which is what every caller
 * actually holds.
 */
export function isHelpless(catalogue, combatant, tick) {
    if (combatant.vitals.dead)
        return true;
    const kit = kitOf(catalogue, combatant);
    // No kit is no body plan, and a body we cannot resolve is not one we may
    // declare helpless — refusing here fails towards leaving the player in
    // control of themselves, which is the safe direction to fail in.
    if (!kit)
        return false;
    return isDown(kit.plan, combatant.vitals, tick);
}
/** Committed — mid-wind-up or mid-recovery, and paying for it in defence. */
export function isCommitted(combatant, tick) {
    return combatant.swing !== null || tick < combatant.recoveryUntil;
}
/**
 * Busy with a slow action (M2-07).
 *
 * Kept apart from `isCommitted` deliberately. `isCommitted` is the question
 * "may this fighter buffer another swing", and a man kneeling over a body is
 * not queuing an attack — he is doing something a swing *cancels*. Folding the
 * two together would have sent his next attack into the input buffer instead of
 * dropping the bandage, which is a mechanic nobody asked for.
 */
export function isChannelling(combatant, tick) {
    return combatant.channel !== null && tick < combatant.channel.completesAt;
}
export function defenceShare(combatant, tick) {
    if (combatant.guarding)
        return DEFENCE_SHARE.guard;
    // Both hands on somebody else's wound is the same defensive position as both
    // hands on a swing: you are not watching for the blow.
    if (isCommitted(combatant, tick) || isChannelling(combatant, tick)) {
        return DEFENCE_SHARE.committed;
    }
    return DEFENCE_SHARE.neutral;
}
/** Stamina spent on an action, scaled by what the fighter is carrying. */
export function spendStamina(combatant, kit, cost) {
    const encumbered = cost * (1 + encumbranceOf(kit.armour) / 4);
    combatant.staminaMilli = Math.max(0, combatant.staminaMilli - Math.round(encumbered * STAMINA_SCALE));
}
//# sourceMappingURL=fighter.js.map